import { calculateCvssBaseScoreFromVector } from './cvss-utils';

export type FindingCvssFields = {
  score?: unknown;
  vector?: string | null;
  version?: string;
  environmentalScore?: unknown;
  environmentalVector?: string | null;
};

export type FindingCvssSource = {
  cvss?: FindingCvssFields | null;
};

export type FindingCvssDisplayValues = {
  score: string | null;
  vector: string | null;
};

export const toNonEmptyString = (value: unknown): string | null => {
  if (typeof value === 'string' && value.trim() !== '') {
    return value;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  return null;
};

const isFullCvssVector = (vector: string): boolean =>
  vector.startsWith('CVSS:');

export function readCvssVectorString(cvss: unknown): string {
  if (typeof cvss === 'string') {
    return isFullCvssVector(cvss) ? cvss : '';
  }

  if (!cvss || typeof cvss !== 'object') {
    return '';
  }

  return toNonEmptyString((cvss as FindingCvssFields).vector) ?? '';
}

const resolveEnvironmentalDisplayVector = ({
  baseVector,
  environmentalVector,
}: {
  baseVector: string | null;
  environmentalVector: string | null;
}): string | null => {
  if (!environmentalVector) {
    return baseVector;
  }

  if (isFullCvssVector(environmentalVector)) {
    return environmentalVector;
  }

  if (baseVector) {
    return `${baseVector}/${environmentalVector}`;
  }

  return environmentalVector;
};

const readCvssField = (
  cvss: FindingCvssFields,
  camelKey: 'environmentalScore' | 'environmentalVector',
): unknown => {
  const direct = cvss[camelKey];

  if (direct !== undefined && direct !== null) {
    return direct;
  }

  const snakeKey =
    camelKey === 'environmentalScore'
      ? 'environmental_score'
      : 'environmental_vector';

  return Reflect.get(cvss, snakeKey);
};

export function getFindingCvssDisplayValues(
  cvss: FindingCvssFields | null | undefined,
): FindingCvssDisplayValues {
  if (!cvss) {
    return { score: null, vector: null };
  }

  const environmentalScore = toNonEmptyString(
    readCvssField(cvss, 'environmentalScore'),
  );
  const baseScore = toNonEmptyString(cvss.score);
  const baseVector = toNonEmptyString(cvss.vector);
  const environmentalVector = toNonEmptyString(
    readCvssField(cvss, 'environmentalVector'),
  );

  if (environmentalScore) {
    return {
      score: environmentalScore,
      vector: resolveEnvironmentalDisplayVector({
        baseVector,
        environmentalVector,
      }),
    };
  }

  return {
    score: baseScore,
    vector: baseVector,
  };
}

export const FINDING_CHAINING_MAX_CHAIN_CVSS_SCORE = 10;

export const FINDING_CHAINING_MIN_CVSS_UPLIFT = 0.1;

function normalizeStoredCvssScore(score: unknown): number | null {
  if (typeof score === 'number' && Number.isFinite(score)) {
    return Math.min(10, Math.max(0, score));
  }

  if (typeof score === 'string' && score.trim() !== '') {
    const n = Number(score);

    if (Number.isFinite(n)) {
      return Math.min(10, Math.max(0, n));
    }
  }

  return null;
}

export function roundCvssScoreToTenth(score: number): number {
  return Math.round(score * 10) / 10;
}

export function getCvssBaseScore0To10ForFinding(f: FindingCvssSource): number {
  const fromEnvironmental = normalizeStoredCvssScore(
    f.cvss?.environmentalScore,
  );

  if (fromEnvironmental !== null) {
    return fromEnvironmental;
  }

  const fromStored = normalizeStoredCvssScore(f.cvss?.score);

  if (fromStored !== null) {
    return fromStored;
  }

  const vector = f.cvss?.vector;

  if (vector && typeof vector === 'string') {
    const fromVector = calculateCvssBaseScoreFromVector(vector);

    if (fromVector !== null) {
      return fromVector;
    }
  }

  return 0;
}

export function canFindingReachMinimumCvssUplift(
  finding: FindingCvssSource,
  minimumUplift: number,
  maxChainScore = FINDING_CHAINING_MAX_CHAIN_CVSS_SCORE,
): boolean {
  return (
    roundCvssScoreToTenth(
      getCvssBaseScore0To10ForFinding(finding) + minimumUplift,
    ) <= maxChainScore
  );
}

export function isTriggerFindingEligibleForChainingAnalysis(
  finding: {
    category?: string | null;
  } & FindingCvssSource,
): boolean {
  if (finding.category === 'VULNERABILITY_CHAINING') {
    return false;
  }

  return canFindingReachMinimumCvssUplift(
    finding,
    FINDING_CHAINING_MIN_CVSS_UPLIFT,
  );
}
